import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";

const RANGE_PRESETS = [
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "90d", label: "Last 90 days" },
  { key: "all", label: "All time" },
  { key: "custom", label: "Custom…" },
];

const AGG_PRESETS = [
  { key: "auto", label: "Auto" },
  { key: "days", label: "Days" },
  { key: "weeks", label: "Weeks" },
  { key: "months", label: "Months" },
  { key: "years", label: "Years" },
];
// Dataset options (frontend labels)
const DATASETS = [
  { key: "subscriptions", label: "Subscriptions" },
  { key: "users", label: "Users" },
  { key: "invites", label: "Invites" },
  { key: "sessions", label: "Sessions" },
  { key: "videos", label: "Videos" },

  { key: "swingAndMiss", label: "Swing And A Miss" },
  { key: "analysisWithIssues", label: "Analysis" },
  { key: "confidenceScore", label: "Confidence Score" },
];

// UI-level data types per dataset
const DATA_TYPES_BY_DATASET = {
  subscriptions: [
    { key: "total", label: "Total" },
    { key: "active", label: "Active" },
    { key: "canceled", label: "Canceled" },
  ],
  users: [{ key: "created", label: "Created" }],
  invites: [{ key: "created", label: "Created" }],
  sessions: [{ key: "created", label: "Created" }],
  videos: [{ key: "uploaded", label: "Uploaded" }],

  swingAndMiss: [
    { key: "total", label: "Swing And A Miss" },
    { key: "percentage", label: "Swing And A Miss by %" },
  ],
  analysisWithIssues: [
    { key: "total", label: "Analysis" },
    { key: "percentage", label: "Analysis by %" },
  ],
  confidenceScore: [
    { key: "bands", label: "Confidence Score" },
    { key: "bandsPct", label: "Confidence Score by %" },
  ],
};

function isPercentMode(dataset, dataType) {
  if (dataset === "swingAndMiss" || dataset === "analysisWithIssues") {
    return dataType === "percentage";
  }
  if (dataset === "confidenceScore") {
    return dataType === "bandsPct";
  }
  return false;
}

function prettySeriesName(name) {
  switch (name) {
    case "issuesFon":
      return "Issues · FON";
    case "issuesDtl":
      return "Issues · DTL";
    case "analysisFon":
      return "Analysis · FON";
    case "analysisDtl":
      return "Analysis · DTL";
    case "cs_89_100":
      return "CS 89–100";
    case "cs_80_89":
      return "CS 80–89";
    case "cs_70_79":
      return "CS 70–79";
    case "cs_60_69":
      return "CS 60–69";
    case "cs_0_59":
      return "CS 0–59";
    default:
      return name;
  }
}

export function GraphView({
  dataset,
  dataType,
  rangeKey,
  aggKey, // --- NEW
  onChangeDataset,
  onChangeType,
  onChangeRange,
  onChangeAgg, // --- NEW
  series,
  loading,
  error,
  cumulative,
  onChangeCumulative,
  customFrom,
  setCustomFrom,
  customTo,
  setCustomTo,
}) {
  const typeOptions = DATA_TYPES_BY_DATASET[dataset] || [];
  const hasData = Array.isArray(series) && series.length > 0;

  const subtitle = useMemo(() => {
    if (dataset === "subscriptions") return "Stacked area by platform.";
    if (dataset === "videos") return "Uploads over time.";
    if (dataset === "sessions") return "Sessions over time.";
    if (dataset === "invites") return "Invites over time.";
    if (dataset === "users") return "Signups over time.";
    if (dataset === "swingAndMiss")
      return "Issues split by FON vs DTL (plus analysis for context).";
    if (dataset === "analysisWithIssues")
      return "Analysis split by FON vs DTL (plus issues for context).";
    if (dataset === "confidenceScore")
      return "Swing confidence score distribution over time.";
    return "Timeseries.";
  }, [dataset]);

  const pctMode = isPercentMode(dataset, dataType);

  const canCumulative = !pctMode;
  const chartData =
    cumulative && canCumulative
      ? makeCumulative(series, keysForDataset(dataset))
      : series;

  function makeCumulative(rows, keys) {
    const totals = Object.fromEntries(keys.map((k) => [k, 0]));
    return rows.map((r) => {
      const next = { ...r };
      for (const k of keys) {
        const v = Number(r[k] ?? 0);
        totals[k] += Number.isFinite(v) ? v : 0;
        next[k] = totals[k];
      }
      return next;
    });
  }

  function keysForDataset(dataset) {
    if (dataset === "subscriptions")
      return ["apple", "android", "stripe", "elavon"];
    if (dataset === "swingAndMiss" || dataset === "analysisWithIssues")
      return ["issuesFon", "issuesDtl", "analysisFon", "analysisDtl"];
    if (dataset === "confidenceScore")
      return ["cs_89_100", "cs_80_89", "cs_70_79", "cs_60_69", "cs_0_59"];
    return ["overall"];
  }

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <Card
        title="Graph controls"
        right={
          <div style={styles.controlRow}>
            <label style={styles.selectLabel}>
              <span style={styles.selectText}>Cumulative</span>
              <input
                type="checkbox"
                checked={cumulative}
                disabled={!canCumulative}
                onChange={(e) => onChangeCumulative(e.target.checked)}
              />
            </label>

            <Select
              label="Dataset"
              value={dataset}
              onChange={onChangeDataset}
              options={DATASETS}
            />
            <Select
              label="Data type"
              value={dataType}
              onChange={onChangeType}
              options={typeOptions}
              disabled={typeOptions.length <= 1}
            />
            <Select
              label="Time range"
              value={rangeKey}
              onChange={onChangeRange}
              options={RANGE_PRESETS}
            />

            <Select
              label="Group by"
              value={aggKey}
              onChange={onChangeAgg}
              options={AGG_PRESETS}
            />

            {rangeKey === "custom" && (
              <div style={styles.customRangeRow}>
                <label style={styles.selectLabel}>
                  <span style={styles.selectText}>From</span>
                  <input
                    type="date"
                    value={customFrom || ""}
                    onChange={(e) => setCustomFrom?.(e.target.value)}
                    style={styles.dateInput}
                  />
                </label>

                <label style={styles.selectLabel}>
                  <span style={styles.selectText}>To</span>
                  <input
                    type="date"
                    value={customTo || ""}
                    onChange={(e) => setCustomTo?.(e.target.value)}
                    style={styles.dateInput}
                  />
                </label>
              </div>
            )}
          </div>
        }
      >
        <div style={{ fontSize: 12, color: "#666" }}>{subtitle}</div>
      </Card>

      <Card title="Over time">
        <div style={{ height: 420 }}>
          {loading ? (
            <div style={styles.stateMsg}>Loading…</div>
          ) : error ? (
            <div style={styles.stateMsg}>
              Failed to load data{error?.message ? `: ${error.message}` : ""}
            </div>
          ) : !hasData ? (
            <div style={styles.stateMsg}>No data for this selection.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={chartData}
                margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} minTickGap={20} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip
                  formatter={(value, name) => {
                    const label = prettySeriesName(name);
                    return [pctMode ? `${value}%` : value, label];
                  }}
                  labelFormatter={(label) => label}
                />
                <Legend formatter={(name) => prettySeriesName(name)} />

                {dataset === "swingAndMiss" ||
                dataset === "analysisWithIssues" ? (
                  <>
                    <Area
                      type="monotone"
                      dataKey="issuesFon"
                      stackId="issues"
                    />
                    <Area
                      type="monotone"
                      dataKey="issuesDtl"
                      stackId="issues"
                    />
                    <Area
                      type="monotone"
                      dataKey="analysisFon"
                      stackId="analysis"
                    />
                    <Area
                      type="monotone"
                      dataKey="analysisDtl"
                      stackId="analysis"
                    />
                  </>
                ) : dataset === "confidenceScore" ? (
                  <>
                    {/* Not stacked so each line reflects its own magnitude */}
                    <Area type="monotone" dataKey="cs_89_100" />
                    <Area type="monotone" dataKey="cs_80_89" />
                    <Area type="monotone" dataKey="cs_70_79" />
                    <Area type="monotone" dataKey="cs_60_69" />
                    <Area type="monotone" dataKey="cs_0_59" />
                  </>
                ) : dataset === "subscriptions" ? (
                  <>
                    <Area type="monotone" dataKey="apple" stackId="subs" />
                    <Area type="monotone" dataKey="android" stackId="subs" />
                    <Area type="monotone" dataKey="stripe" stackId="subs" />
                    <Area type="monotone" dataKey="elavon" stackId="subs" />
                  </>
                ) : (
                  <Area type="monotone" dataKey="overall" />
                )}
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>
    </div>
  );
}

function Select({ label, value, onChange, options, disabled }) {
  return (
    <label style={styles.selectLabel}>
      <span style={styles.selectText}>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={styles.select}
        disabled={disabled}
      >
        {options.map((o) => (
          <option key={o.key} value={o.key}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function Card({ title, right, children }) {
  return (
    <div style={styles.card}>
      {(title || right) && (
        <div style={styles.cardHeader}>
          <div style={{ fontWeight: 600 }}>{title}</div>
          <div>{right}</div>
        </div>
      )}
      {children}
    </div>
  );
}

const styles = {
  controlRow: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    flexWrap: "wrap",
  },
  customRangeRow: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    flexWrap: "wrap",
  },
  selectLabel: { display: "flex", gap: 8, alignItems: "center" },
  selectText: { fontSize: 12, color: "#444", minWidth: 70 },
  select: {
    border: "1px solid #ddd",
    borderRadius: 10,
    padding: "8px 10px",
    background: "#fff",
    minWidth: 160,
  },
  dateInput: {
    border: "1px solid #ddd",
    borderRadius: 10,
    padding: "8px 10px",
    background: "#fff",
    minWidth: 160,
  },
  card: {
    border: "1px solid #e6e6e6",
    borderRadius: 16,
    padding: 14,
    background: "#fff",
    boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 10,
    gap: 12,
  },
  stateMsg: {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#666",
    fontSize: 13,
  },
};
